// ─── Recibir dinero México · Side panel (SPEI) ──────────────────────────────────────────────────────────
// The MXN wallet's receive panel: the COP panel's drawer for the one SPEI rail (`RailDepositDrawer`).
//
// Every SPEI payin is dynamic. The provider does not give an organization a standing CLABE — it lends one
// from a pool shared across tenants for each charge and takes it back when the charge expires — so there is
// nothing to publish up front: the CLABE in the result belongs to THIS payment, for the amount shown, until
// it expires.
//
// Uses the NestJS payments runtime (`POST /internal/payments/payins`) — fees are computed server-side.

import { Landmark } from "lucide-react";

import {
  buildMexicoSpeiPayinPayload,
  PayinRail,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";

import type { Wallet } from "../types/wallets-api.types";
import type { MexicoPayerValues } from "./deposit/depositPayer.schema";
import { MexicoPayerStep } from "./deposit/MexicoPayerStep";
import { type RailDepositCorridor, RailDepositDrawer } from "./deposit/RailDepositDrawer";
import { SpeiPaymentInstrument } from "./deposit/SpeiPaymentInstrument";

export interface MexicoDepositModalProps {
  wallet: Wallet;
  open: boolean;
  onClose: () => void;
}

const MEXICO_SPEI: RailDepositCorridor<MexicoPayerValues> = {
  rail: PayinRail.SPEI,
  country: "MEX",
  currency: "MXN",
  amountHintKey: "deposit_amount_hint_mex",
  methodHintKey: "deposit_method_spei_hint",
  processingKey: "deposit_processing_spei",
  methodIcon: <Landmark className="size-5" />,
  initialPayer: (user) => ({ payerName: user?.full_name ?? "", rfc: "" }),
  PayerStep: MexicoPayerStep,
  payerRows: (payer, t) => [
    { label: t("deposit_confirm_name"), value: payer.payerName.trim() },
    ...(payer.rfc.trim() ? [{ label: t("deposit_payer_rfc_short"), value: payer.rfc.trim() }] : []),
  ],
  // Measured raw, as the backend does: a 12-character RFC is a company.
  payerType: (payer) => (payer.rfc.trim() ? (payer.rfc.trim().length === 12 ? "COMPANY" : "PERSON") : null),
  buildPayload: ({ organizationId, grossAmount, payer, idempotencyKey }) =>
    buildMexicoSpeiPayinPayload({
      organizationId,
      amountMxn: grossAmount,
      payerName: payer.payerName,
      payerDocument: payer.rfc.trim() || undefined,
      idempotencyKey,
    }),
  Instrument: SpeiPaymentInstrument,
};

export function MexicoDepositModal({ wallet, open, onClose }: MexicoDepositModalProps) {
  return <RailDepositDrawer wallet={wallet} open={open} onClose={onClose} corridor={MEXICO_SPEI} />;
}
